import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { CheckIcon, EditIcon } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/base/button";
import { DataTable, type DataTableColumn } from "@/components/base/data-table";
import { subtitle, title } from "@/components/base/primitives";
import { Toolbar } from "@/components/base/toolbar";
import { PoolFinishInfo } from "@/components/pools/finish-info";
import { ProfileName } from "@/components/profiles/name";
import { EditSeedForm } from "@/components/teams/controls/edit-seed";
import type { Tournament, TournamentDivision } from "@/db/schema";
import {
  getPoolStats,
  isPoolComplete,
  poolHasPendingMatches,
} from "@/hooks/matches";
import { usePanelModals } from "@/hooks/panel-modals";
import { getLevelDisplay, getPlayerLevel } from "@/hooks/tournament";
import { useCompletePoolsMutation } from "@/hooks/tournament-mutations";
import { useCheckIn } from "@/hooks/use-check-in";
import { useViewerPoolIds } from "@/hooks/viewer-pools";
import { useTRPC } from "@/integrations/trpc/react";
import { isNotNullOrUndefined } from "@/utils/types";

import { CheckInButton } from "../check-in-button";
import {
  useActiveTeams,
  useCanUpdateTournament,
  useTeamMap,
  useTournamentDivision,
} from "../context";
import { CreatePoolMatchesForm } from "../controls/create-pool-matches";
import { CreatePoolsForm } from "../controls/create-pools";
import { SetCourtForm } from "../controls/set-court";

export const PoolsPanel = memo(function PoolsPanel({
  id,
  tournamentDivisionId,
  isActive,
}: Pick<Tournament, "id"> & {
  tournamentDivisionId: TournamentDivision["id"];
  isActive: boolean;
}) {
  const trpc = useTRPC();

  const canEdit = useCanUpdateTournament();

  const search = useSearch({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });

  const navigate = useNavigate({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });

  const [editPoolId, setEditPoolId] = useState<number | undefined>(undefined);

  const { data } = useQuery({
    ...trpc.tournaments.getPools.queryOptions({ tournamentDivisionId }),
    enabled: isActive,
  });

  const { checkInMap, toggleCheckIn } = useCheckIn({
    tournamentDivisionId,
    enabled: isActive && canEdit && data != null,
  });

  const { teamMap } = useTeamMap();

  const poolFilterValues = useMemo(() => new Set(search.pools), [search.pools]);

  const { viewerProfileIds, viewerPoolIds, isViewerPending } = useViewerPoolIds(
    data,
    teamMap,
  );

  const completePools = useCompletePoolsMutation();

  const pools = useMemo(() => {
    return data?.map((pool) => ({
      pool,
      stats: getPoolStats(pool),
      hasPendingMatches: poolHasPendingMatches(pool.matches),
      isComplete: isPoolComplete(pool.teams),
    }));
  }, [data]);

  // Auto-select the viewer's pool when they're a player (not a TD)
  const hasAppliedViewer = useRef(false);
  useEffect(() => {
    if (!data || isViewerPending || hasAppliedViewer.current) return;
    hasAppliedViewer.current = true;
    if (!canEdit && viewerPoolIds.size > 0 && poolFilterValues.size === 0) {
      navigate({
        replace: true,
        resetScroll: false,
        search: (prev) => ({
          ...prev,
          pools: [...viewerPoolIds],
        }),
      });
    }
  }, [
    data,
    isViewerPending,
    canEdit,
    viewerPoolIds,
    navigate,
    poolFilterValues.size,
  ]);

  const visiblePools = useMemo(() => {
    if (poolFilterValues.size === 0) return pools ?? [];
    return pools?.filter(({ pool }) => poolFilterValues.has(pool.id)) ?? [];
  }, [pools, poolFilterValues]);

  const division = useTournamentDivision();
  const { setActiveModal, modalProps } = usePanelModals<
    "createPools" | "createPoolMatches"
  >();

  function togglePool(poolId: number) {
    const isSelected =
      poolFilterValues.has(poolId) && poolFilterValues.size === 1;
    navigate({
      replace: true,
      resetScroll: false,
      search: (prev) => ({
        ...prev,
        pools: isSelected ? undefined : [poolId],
      }),
    });
  }

  const teams = useActiveTeams(tournamentDivisionId);
  const hasTeams = teams && teams.length > 0;
  const hasPools = data && data.length > 0;
  const hasPoolMatches = data?.some((p) => p.matches.length > 0) ?? false;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-10 px-3 pt-6 pb-12">
      <div className="flex flex-col gap-4">
        {canEdit && division && (
          <>
            <Toolbar
              aria-label="Pool controls"
              className="ml-auto flex w-fit items-center gap-2"
            >
              <Button
                variant={hasPools ? "outline" : "solid"}
                color={hasPools ? undefined : "primary"}
                isDisabled={!hasTeams}
                onPress={() => setActiveModal("createPools")}
              >
                {hasPools ? "Recreate Pools" : "Create Pools"}
              </Button>
              <Button
                variant={hasPoolMatches ? "outline" : "solid"}
                color={hasPoolMatches ? undefined : "primary"}
                isDisabled={!hasPools}
                onPress={() => setActiveModal("createPoolMatches")}
              >
                {hasPoolMatches
                  ? "Recreate Pool Matches"
                  : "Create Pool Matches"}
              </Button>
            </Toolbar>
            <CreatePoolsForm
              tournamentId={id}
              division={division}
              teamCount={teams?.length}
              {...modalProps("createPools")}
            />
            <CreatePoolMatchesForm
              tournamentId={id}
              division={division}
              {...modalProps("createPoolMatches")}
            />
          </>
        )}
        {!hasPools && (
          <p className="py-8 text-center text-gray-500">
            No pools have been created yet.
          </p>
        )}
        {data && data.length > 1 && (
          <div className="flex flex-wrap items-center gap-2">
            {data.map((pool) => {
              const isSelected = poolFilterValues.has(pool.id);
              const isViewerPool = viewerPoolIds.has(pool.id);
              return (
                <Button
                  key={pool.id}
                  variant={isSelected ? undefined : "outline"}
                  color={isViewerPool ? "primary" : "secondary"}
                  size="sm"
                  onPress={() => togglePool(pool.id)}
                >
                  Pool {pool.name.toUpperCase()}
                  {pool.court ? ` — ${pool.court}` : ""}
                </Button>
              );
            })}
          </div>
        )}
      </div>

      {visiblePools.map(({ pool, stats, hasPendingMatches, isComplete }) => {
        const allMatchesDone = !hasPendingMatches && pool.matches.length > 0;
        const orderedTeams =
          stats && allMatchesDone
            ? pool.teams.toSorted((a, b) => {
                return (
                  (stats[a.teamId]?.rank ?? 0) - (stats[b.teamId]?.rank ?? 0)
                );
              })
            : pool.teams;

        type PoolTeam = (typeof orderedTeams)[number];

        const getRowClassName = (item: PoolTeam) => {
          const tdt = teamMap.get(item.teamId);
          if (
            tdt?.team.players.some((p) => viewerProfileIds.has(p.profile.id))
          ) {
            return "bg-blue-50 hover:bg-blue-100";
          }
          return undefined;
        };

        const finishCell = (item: PoolTeam) => {
          const teamId = item.teamId;
          return (
            <div className="flex flex-row items-center justify-center gap-2">
              <span>{item.finish ?? stats?.[teamId]?.rank ?? "-"}</span>
              {(item.finish ?? stats?.[teamId]?.rank) && (
                <PoolFinishInfo poolId={pool.id} activeTeamId={teamId} />
              )}
            </div>
          );
        };

        const mobileColumns: DataTableColumn<PoolTeam>[] = [
          ...(allMatchesDone
            ? ([
                {
                  id: "finish",
                  header: "",
                  width: 50,
                  cell: finishCell,
                },
              ] as DataTableColumn<PoolTeam>[])
            : []),
          {
            id: "team",
            header: "Team",
            isRowHeader: true,
            cell: (item) => {
              const tdt = teamMap.get(item.teamId);
              return (
                <span
                  className={twMerge(
                    "flex flex-col gap-1",
                    tdt?.forfeited && "opacity-50",
                  )}
                >
                  {tdt?.team.players.map((tp) => (
                    <span key={tp.profile.id} className="flex items-center">
                      {canEdit && (
                        <CheckInButton
                          checkIn={checkInMap.get(tp.profile.id)}
                          playerProfileId={tp.profile.id}
                          toggleCheckIn={toggleCheckIn}
                        />
                      )}
                      <ProfileName {...tp.profile} />{" "}
                      <span className="text-gray-500">
                        ({getLevelDisplay(getPlayerLevel(tp))})
                      </span>
                    </span>
                  ))}
                </span>
              );
            },
          },
          ...(canEdit
            ? []
            : ([
                {
                  id: "record",
                  header: "W/L",
                  width: 60,
                  cell: (item: PoolTeam) => {
                    const teamStats = stats?.[item.teamId];
                    return `${teamStats?.wins.size ?? "-"}/${teamStats?.losses.size ?? "-"}`;
                  },
                },
              ] as DataTableColumn<PoolTeam>[])),
        ];

        function renderMobileExpanded(item: PoolTeam) {
          const teamId = item.teamId;
          const teamStats = stats?.[teamId];

          const details: [string, React.ReactNode][] = [
            [
              "Seed",
              <span key="seed" className="flex flex-row items-center gap-4">
                <span>{item.seed ?? "-"}</span>
                {editPoolId === pool.id && item.seed && (
                  <EditSeedForm
                    tournamentDivisionTeamId={teamId}
                    seed={item.seed}
                    target="pool"
                  />
                )}
              </span>,
            ],
            ["Wins", String(teamStats?.wins.size ?? "-")],
            ["Losses", String(teamStats?.losses.size ?? "-")],
          ];

          return (
            <div className="flex flex-col text-sm">
              {details.map(([label, value]) => (
                <div
                  key={label}
                  className="flex items-center justify-between px-2 py-1.5 odd:bg-gray-100"
                >
                  <span className="font-medium">{label}</span>
                  <span>{value}</span>
                </div>
              ))}
            </div>
          );
        }

        const desktopColumns: DataTableColumn<PoolTeam>[] = [
          ...(allMatchesDone
            ? ([
                {
                  id: "finish",
                  header: "Finish",
                  allowsSorting: true,
                  minWidth: 100,
                  cell: finishCell,
                },
              ] as DataTableColumn<PoolTeam>[])
            : []),
          {
            id: "seed",
            header: "Seed",
            isRowHeader: true,
            allowsSorting: true,
            minWidth: 100,
            cell: (item) => {
              const teamId = item.teamId;
              return (
                <div className="flex flex-row items-center gap-4">
                  <span>{item.seed ?? "-"}</span>
                  {editPoolId === pool.id && item.seed && (
                    <EditSeedForm
                      tournamentDivisionTeamId={teamId}
                      seed={item.seed}
                      target="pool"
                    />
                  )}
                </div>
              );
            },
          },
          {
            id: "team",
            header: "Team",
            isRowHeader: true,
            minWidth: 90,
            cell: (item) => {
              const tdt = teamMap.get(item.teamId);
              return (
                <span
                  className={twMerge(
                    "flex flex-col gap-1 md:flex-row md:items-center",
                    tdt?.forfeited && "opacity-50",
                  )}
                >
                  {tdt?.team.players.map((tp, i) => (
                    <span
                      key={tp.profile.id}
                      className="inline-flex items-center"
                    >
                      {canEdit && (
                        <CheckInButton
                          checkIn={checkInMap.get(tp.profile.id)}
                          playerProfileId={tp.profile.id}
                          toggleCheckIn={toggleCheckIn}
                        />
                      )}
                      <ProfileName {...tp.profile} />{" "}
                      <span className="text-gray-500">
                        ({getLevelDisplay(getPlayerLevel(tp))})
                      </span>
                      {i === (tdt?.team.players.length ?? 0) - 1 ? null : (
                        <span className="hidden md:inline-block"> & </span>
                      )}
                    </span>
                  ))}
                  {tdt?.forfeited && (
                    <span className="text-xs font-medium text-red-500">
                      Forfeit
                    </span>
                  )}
                </span>
              );
            },
          },
          {
            id: "wins",
            header: "Wins",
            isRowHeader: true,
            minWidth: 90,
            cell: (item) => stats?.[item.teamId]?.wins.size ?? "-",
          },
          {
            id: "losses",
            header: "Losses",
            isRowHeader: true,
            minWidth: 90,
            cell: (item) => stats?.[item.teamId]?.losses.size ?? "-",
          },
        ];

        const isViewerPool = viewerPoolIds.has(pool.id);

        return (
          <section key={pool.id}>
            <div className="mb-2 flex flex-col gap-y-1">
              <div className="flex items-center gap-x-4">
                <h2
                  className={title({
                    size: "sm",
                    className: `uppercase ${isViewerPool ? "text-blue-700" : ""}`,
                  })}
                >
                  Pool {pool.name}
                </h2>
                <span
                  className={`text-sm font-medium ${isViewerPool ? "text-blue-600" : "text-gray-500"}`}
                >
                  {orderedTeams.length} teams
                </span>
              </div>
              <div className="flex items-center">
                <span className={subtitle()}>
                  <SetCourtForm
                    tournamentId={id}
                    tournamentDivisionId={tournamentDivisionId}
                    poolId={pool.id}
                    name={`Pool ${pool.name.toUpperCase()}`}
                    court={pool.court}
                  />
                </span>
                {canEdit && (
                  <div className="ml-auto flex items-center gap-1.5">
                    {allMatchesDone && (
                      <Button
                        variant={isComplete ? "outline" : "solid"}
                        color={isComplete ? undefined : "primary"}
                        size="sm"
                        className="whitespace-nowrap"
                        isDisabled={completePools.isPending}
                        onPress={() =>
                          completePools.mutate({
                            id: tournamentDivisionId,
                            poolIds: [pool.id],
                          })
                        }
                      >
                        {isComplete ? "Reverify Pool" : "Verify Pool"}
                      </Button>
                    )}
                    <Button
                      variant="icon"
                      onPress={() =>
                        setEditPoolId((curr) =>
                          isNotNullOrUndefined(curr) ? undefined : pool.id,
                        )
                      }
                    >
                      {editPoolId === pool.id ? (
                        <CheckIcon size={12} />
                      ) : (
                        <EditIcon size={12} />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {isActive && (
              <div className="flex flex-col">
                {/* Mobile */}
                <div className="w-full md:hidden">
                  <DataTable
                    aria-label={`Pool ${pool.name.toUpperCase()}`}
                    data={orderedTeams}
                    columns={mobileColumns}
                    getRowId={(t) => t.id}
                    renderExpandedContent={renderMobileExpanded}
                    getRowClassName={getRowClassName}
                  />
                </div>

                {/* Desktop */}
                <div className="hidden w-full md:block">
                  <DataTable
                    aria-label={`Pool ${pool.name.toUpperCase()}`}
                    data={orderedTeams}
                    columns={desktopColumns}
                    getRowId={(t) => t.id}
                    getRowClassName={getRowClassName}
                  />
                </div>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
});
